/**
 * Auth0Callback — landing page after Auth0 redirects back with the
 * authorization code in the query string. The Auth0 SDK auto-processes
 * the code on mount; we just show a spinner and bounce the user.
 *
 * Mounted at `/auth/callback`. Only reachable when AUTH_PROVIDER is
 * `auth0` or `both`. In `emergent` mode this route never renders
 * because the BrowserRouter intercepts `#session_id=...` hashes via
 * AuthCallback.jsx first.
 */
import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";

const Auth0Callback = () => {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const navigate = useNavigate();
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      navigate("/admin", { replace: true });
    } else if (error) {
      // Auth0 SDK gives us a structured error here.
      // Surface it; user can retry from /admin/login.
    } else {
      // Auth0 processed the code but did not produce a session — bounce.
      const t = setTimeout(() => setStuck(true), 4000);
      return () => clearTimeout(t);
    }
  }, [isAuthenticated, isLoading, error, navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-black text-white px-6"
      data-testid="auth0-callback"
    >
      <div className="max-w-md text-center">
        {error ? (
          <>
            <h1 className="ek-heading text-2xl mb-3">Sign-in failed</h1>
            <p className="text-zinc-300 mb-6">{error.message || String(error)}</p>
            <button
              onClick={() => navigate("/admin/login", { replace: true })}
              className="ek-btn ek-btn-primary"
              data-testid="auth0-callback-retry"
            >
              Try again
            </button>
          </>
        ) : stuck ? (
          <>
            <p className="text-zinc-300 mb-6">
              Hmm — sign-in is taking longer than expected.
            </p>
            <button
              onClick={() => navigate("/admin/login", { replace: true })}
              className="ek-btn ek-btn-primary"
              data-testid="auth0-callback-retry"
            >
              Try again
            </button>
          </>
        ) : (
          <>
            <div
              className="w-10 h-10 rounded-full border-2 border-white/30 border-t-white animate-spin mx-auto mb-5"
              aria-hidden="true"
            />
            <p className="text-zinc-300 text-sm tracking-[0.16em] uppercase">
              Signing you in…
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Auth0Callback;
